<template>
  <BaseDialog
    :visible="open"
    @update:open="(val) => emit('update:open', val)"
    :width="dialogWidth"
    :dialogClass="windowWidth < 768 ? 'mobile-dialog' : ''"
    :draggable="false"
    @keydown.esc="emit('update:open', false)"
    role="dialog"
    aria-modal="true"
    aria-labelledby="restore-passphrase-title"
  >
    <template #header>
      <!-- Mobile: top row with back + close, title below -->
     <div v-if="windowWidth < 768" class="w-full">
    <div class="flex justify-between items-center pt-5 px-5">
      <button
        @click="onBack"
        @keydown.enter="onBack"
        class="cursor-pointer border-none bg-transparent p-2 -ml-1"
        style="margin-top: 2px" 
        v-html="backSvg"
        tabindex="0"
        :aria-label="$t('accessibilityLabels.goBack')"
      ></button>
      <button
        @click="$emit('update:open', false)"
        @keydown.enter="$emit('update:open', false)"
        class="cursor-pointer border-none bg-transparent p-2 -mr-2"
        v-html="cancelSvg"
        tabindex="0"
        :aria-label="$t('accessibilityLabels.closeDialog')"
      ></button>
    </div>
    <div class="px-5 pt-5 pb-5">
      <h2 id="restore-passphrase-title" class="!text-[24px] !font-just font-normal text-btnActive !tracking-[0%] !leading-[100%] flex items-center gap-2">
        <span v-html="IconPassphrase"></span>
        {{ $t('securityModule.dialog.restorePassphrase.title') }}
      </h2>
    </div>
  </div>
      <!-- Desktop: original layout -->
      <div v-else class="flex justify-between items-center p-6 w-full">
        <h2
          id="restore-passphrase-title"
          class="!text-[24px] md:!text-[30.72px] !font-just font-normal text-btnActive !tracking-[0%] !leading-[100%] flex items-center gap-2"
        >
          <span v-html="IconPassphrase"></span>
          {{ $t('securityModule.dialog.restorePassphrase.title') }}
      </h2>
        <button
          @click="$emit('update:open', false)"
          @keydown.enter="$emit('update:open', false)"
          class="cursor-pointer border-none bg-transparent p-2 flex items-center"
          v-html="cancelSvg"
          tabindex="0"
          :aria-label="$t('accessibilityLabels.closeDialog')"
          :autofocus="visible ? true : false"
        ></button>
      </div>
    </template>
    <div class="bg-white px-5 pb-6 rounded-[23.04px] font-just">
      <div class="mb-6">
        <div class="border-[1px] border-solid border-whiteBlue"></div>
      </div>
      <div class="flex flex-col items-start justify-center gap-8">
        <p
          class="text-[16px] md:text-[20px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
          v-html="$t('securityModule.dialog.restorePassphrase.subtitle')"
        ></p>

        <div class="flex flex-col items-start gap-2 text-xs mb-2 w-full">
          <BaseInput
            ref="passphraseInput"
            id="restore-passphrase-input"
            v-model="passphrase"
            :label="$t('securityModule.dialog.restorePassphrase.currentPassphraseLabel')"
            :labelClass="'!text-grayOverlay/60 !text-[14px]'"
            :labelState="false"
            :rows="4"
            :textarea="true"
            class="w-full !border-lightGray"
            auto-grow
            aria-required="true"
          />

          <span v-if="encrypting" class="flex items-center gap-2" role="status" aria-live="polite">
            <span class="animate-spin" v-html="loadingIcon()"></span>
            <span
              class="text-[16px] font-just font-normal text-deepSky tracking-[0%] leading-[100%]"
            >
              {{ $t('securityModule.dialog.restorePassphrase.checkingPassphrase') }}</span
            >
          </span>
        </div>
      </div>
    </div>
    <div v-if="securityStore.errorMsg" role="alert" aria-live="assertive">
      <span
        class="text-base my-2 !font-just text-error flex justify-center"
        data-test-id="restore-passphrase-errorMessage"
      >
        {{ securityStore.errorMsg }}
      </span>
    </div>
    <template #footer>
      <div class="w-full p-6 px-5">
        <BaseButton
          class="dialog-btn dialog-btn--accept !bg-btnActive w-full disabled:!bg-grayOverlay"
          :label="$t('commonLabels.continue')"
          :disabled="!passphrase || encrypting || securityStore.loading"
          :loading="encrypting || securityStore.loading"
          @click="onSubmit"
        />
      </div>
    </template>
  </BaseDialog>

  <LoseEncryptedDataDialog
    :open="loseEncryptedDataDialogOpen"
    @update:open="closeLoseEncryptedDataDialog"
  />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import BaseDialog from "@/components/Base/BaseDialog.vue";
import BaseInput from "@/components/Base/BaseInput.vue";
import BaseButton from "@/components/Base/BaseButton.vue";
import { ROLES_MODULE } from "@/utils/label.js";
import { closeSvg, IconPassphrase, loadingIcon,backArrowSvg } from "@/utils/commonSvg";
import { useSecurityStore } from "@/stores/securityStore";
import { verifyPassphrase } from "@/utils/common";
import LoseEncryptedDataDialog from "./LoseEncryptedDataDialog.vue";
import { useI18n } from "vue-i18n";

// Initialize security store for encryption operations
const securityStore = useSecurityStore();
const router = useRouter();
const { t } = useI18n();

// Component props definition
const props = defineProps({
  // Controls the visibility of the dialog
  open: {
    type: Boolean,
    required: true,
  },
});

// Emits custom events to notify parent about dialog state and asset actions
// - "update:open": Dialog visibility change
// - "submit": Passphrase successfully created
const emit = defineEmits(["update:open", "submit"]);

// State management
const windowWidth = ref(window.innerWidth);
const passphrase = ref("");
const hint = ref("");
const encrypting = ref(false);
const passphraseInput = ref(null);

// Computed properties for UI elements
const cancelSvg = computed(() => closeSvg({ width: "24px", height: "24px" }));
const backSvg = computed(() =>
  backArrowSvg({ width: "24", height: "24px", color: "#818181" })
);

// Responsive dialog width (full width on mobile, 50% on desktop)
const dialogWidth = computed(() => {
  return windowWidth.value < 768 ? "100%" : "50%";
});

/**
 * Update window dimensions for responsive behavior
 */
function handleResize() {
  windowWidth.value = window.innerWidth;
}

// Back: close dialog and navigate to Security page
function onBack() {
  emit("update:open", false);
  router.push("/security");
}

/**
 * Fetches encryption status and hint from the security store
 * Only makes the API call if the hint is not already available
 */
const getEncryption = async () => {
  if (!securityStore.encryptionDetails?.hint) {
    encrypting.value = true;

    try {
      await securityStore.getEncryptionStatus();
      hint.value = securityStore.encryptionDetails?.hint;
      securityStore.hint = hint.value;
    } catch (error) {
      console.error("Error fetching encryption status:", error);
    } finally {
      encrypting.value = false;
    }
  }
};

// State for LoseEncryptedDataDialog visibility
const loseEncryptedDataDialogOpen = ref(false);

// Function to open LoseEncryptedDataDialog
function openLoseEncryptedDataDialog() {
  loseEncryptedDataDialogOpen.value = true;
}

// Function to close LoseEncryptedDataDialog
function closeLoseEncryptedDataDialog() {
  loseEncryptedDataDialogOpen.value = false;
}

// Lifecycle hooks
onMounted(() => {
  // Initialize component when mounted
  getEncryption();
  window.addEventListener("resize", handleResize);
  // Auto-focus passphrase input when dialog opens
  setTimeout(() => {
    if (passphraseInput.value) {
      passphraseInput.value.$el?.querySelector('textarea')?.focus();
    }
  }, 100);
});

onBeforeUnmount(() => {
  // Clean up event listeners when component is unmounted
  window.removeEventListener("resize", handleResize);
});

/**
 * Handle form submission
 * 1. Stores the entered passphrase in the security store
 * 2. Verifies the passphrase against the stored encryption key
 * 3. Emits 'submit' event with the verification result (always emits, even if wrong)
 * 4. Opens LoseEncryptedDataDialog if the passphrase is wrong
 */
async function onSubmit() {
  encrypting.value = true;
  try {
    // Store passphrase in security store
    securityStore.passphrase = passphrase.value;

    // Verify passphrase against stored encryption key
    const result = await verifyPassphrase(
      passphrase.value,
      securityStore.encryptionDetails?.encryptedEncryptionKey
    );

    if (!result) {
      // If passphrase is wrong, open LoseEncryptedDataDialog
      openLoseEncryptedDataDialog();
    }

    // Always emit submit event with result (success or failure)
    setTimeout(() => {
      emit("submit", { result: { valid: !!result }, passphrase: passphrase.value });
      encrypting.value = false;
    }, 2500);
  } catch (error) {
    // On error, emit with invalid result
    setTimeout(() => {
      emit("submit", { result: { valid: false }, passphrase: passphrase.value });
      encrypting.value = false;
    }, 2500);
  } finally {
    // Always reset the encrypting state
    setTimeout(() => {
      encrypting.value = false;
    }, 2500);
  }
}
</script>

<style>
.mobile-dialog {
  height: 100% !important;
  max-height: 100% !important;
}
</style>
